import {
  AlertCircle,
  Building2,
  CalendarClock,
  Camera,
  CheckSquare,
  ClipboardList,
  DoorOpen,
  FileText,
  History,
  Image as ImageIcon,
  Phone,
  Plus,
  Trash2,
  User,
  X
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// 섹션별 점검 항목 구조
const CHECKLIST_SECTIONS = [
  {
    name: '현관',
    items: ['도어락', '신발장', '바닥', '센서등']
  },
  {
    name: '거실',
    items: ['바닥', '벽지', '천장', '에어컨', '조명']
  },
  {
    name: '안방/침실',
    items: ['문 상태', '창문/방충망', '바닥', '벽지']
  },
  {
    name: '주방',
    items: ['싱크대', '상·하부장', '가스레인지/인덕션', '환기', '누수']
  },
  {
    name: '욕실',
    items: ['변기', '세면대', '거울', '샤워부스', '타일']
  },
  {
    name: '옵션',
    items: ['에어컨', '냉장고', '가스레인지/인덕션', '전자레인지', 'TV', '인터넷'],
    hasNoneOption: true
  },
  {
    name: '검침',
    items: ['전기', '가스', '수도', '난방'],
    isMeter: true
  }
];

// 전체 항목 목록 생성 (하위 호환성)
const getAllItems = () => {
  const items = [];
  CHECKLIST_SECTIONS.forEach(section => {
    section.items.forEach(item => {
      items.push(`${section.name}-${item}`);
    });
  });
  return items;
};

const MOVE_IN_ITEMS = getAllItems();
const MOVE_OUT_ITEMS = MOVE_IN_ITEMS;

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR');
};

// 사인 패드 컴포넌트
const SignaturePad = ({ value, onChange, label }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 모바일 최적화: 화면 크기에 맞게 캔버스 크기 조정
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = Math.min(rect.width - 32, 400);
    canvas.height = 200;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasSignature(true);
      };
      img.src = value;
    }
  }, [value]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        onChange(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onChange('');
      setHasSignature(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="border-2 border-gray-300 rounded-lg bg-white relative overflow-hidden">
        <canvas
          ref={canvasRef}
          width={400}
          height={200}
          className="w-full h-48 sm:h-52 touch-none cursor-crosshair"
          style={{ touchAction: 'none' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none">
            여기에 서명해주세요
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={clearSignature}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
        >
          <X className="w-4 h-4" />
          <span>지우기</span>
        </button>
      </div>
    </div>
  );
};

const getInitialMoveInForm = () => {
  const checklist = {};
  CHECKLIST_SECTIONS.forEach(section => {
    section.items.forEach(item => {
      const key = `${section.name}-${item}`;
      if (section.isMeter) {
        // 검침은 숫자 입력
        checklist[key] = '';
      } else {
        // 일반 항목은 상태 선택
        checklist[key] = '양호';
      }
    });
  });
  return {
    inspector: '',
    report_date: new Date().toISOString().substring(0, 10),
    property_owner_id: '',
    building_id: '',
    unit_id: null,
    unit_number: '',
    tenant_name: '',
    phone: '',
    expected_date: '',
    rent_info: [
      { label: '월세', detail: '선불', amount: 500000 },
      { label: '관리비', detail: '선불', amount: 1200000 },
      { label: '주차비', detail: '선불', amount: 50000 },
      { label: '보증금', detail: '보증금', amount: 5000000 }
    ],
    checklist,
    notes: '',
    photos: [],
    signature: ''
  };
};

const getInitialMoveOutForm = () => {
  const checklist = {};
  CHECKLIST_SECTIONS.forEach(section => {
    section.items.forEach(item => {
      const key = `${section.name}-${item}`;
      if (section.isMeter) {
        // 검침은 숫자 입력 (입실/퇴실 값)
        checklist[key] = { moveInValue: '', moveOutValue: '' };
      } else {
        // 일반 항목은 상태 선택
        checklist[key] = { moveInStatus: '양호', moveOutStatus: '양호' };
      }
    });
  });
  return {
    inspector: '',
    report_date: new Date().toISOString().substring(0, 10),
    property_owner_id: '',
    building_id: '',
    unit_id: null,
    unit_number: '',
    tenant_name: '',
    phone: '',
    expected_date: '',
    dues: [
      { label: '미납 월세', detail: '2개월분', amount: 1000000 },
      { label: '미납관리비 등', detail: '2개월분', amount: 200000 },
      { label: '기타', detail: '퇴실청소비 등', amount: 150000 },
      { label: '보증금 반환', detail: '보증금', amount: -5000000 }
    ],
    checklist,
    notes: '',
    photos: [],
    signature: ''
  };
};

const MoveInOutManagement = () => {
  const { user } = useAuth();
  const isRMCUser =
    user?.role === 'rental_management_company' ||
    user?.role === 'rmc_admin' ||
    user?.role === 'rmc_staff' ||
    (user?.user_type === 'rmc_user' && user?.role === 'admin');

  const [activeTab, setActiveTab] = useState('schedule'); // schedule, move_in, move_out, history
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [filters, setFilters] = useState({
    property_owner_id: '',
    building_id: '',
    month: new Date().toISOString().slice(0, 7),
    type: ''
  });
  const [scheduleList, setScheduleList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [moveInForm, setMoveInForm] = useState(getInitialMoveInForm());
  const [moveOutForm, setMoveOutForm] = useState(getInitialMoveOutForm());
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // 모달 상태
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('move_in'); // move_in or move_out
  const [modalForm, setModalForm] = useState({
    property_owner_id: '',
    building_id: '',
    unit_id: '',
    unit_number: '',
    tenant_name: '',
    phone: '',
    expected_date: '',
    inspector: '',
    summary: '',
    notes: ''
  });
  const [modalUnits, setModalUnits] = useState([]); // 모달용 세대 목록
  const [loadingModalUnits, setLoadingModalUnits] = useState(false);
  
  // 스케줄 목록 갱신을 위한 ref (무한 루프 방지)
  const scheduleRefreshRef = useRef(false);

  useEffect(() => {
    if (!isRMCUser) return;
    const fetchOwners = async () => {
      try {
        setLoadingOwners(true);
        const response = await api.get('/rmc/property-owners');
        const owners =
          (response?.property_owners) ||
          (response?.data?.property_owners) ||
          (Array.isArray(response) ? response : []);
        setPropertyOwners(owners);
      } catch (error) {
        console.error('임대사업자 목록 불러오기 실패:', error);
        setPropertyOwners([]);
      } finally {
        setLoadingOwners(false);
      }
    };
    fetchOwners();
  }, [isRMCUser]);

  useEffect(() => {
    if (!isRMCUser || !filters.property_owner_id) {
      setBuildings([]);
      return;
    }
    const fetchBuildings = async () => {
      try {
        setLoadingBuildings(true);
        const params = new URLSearchParams();
        if (filters.property_owner_id) params.append('property_owner_id', filters.property_owner_id);
        const response = await api.get(`/rmc/buildings?${params.toString()}`);
        // API 인터셉터가 response.data를 반환하므로 response 자체가 이미 data입니다
        const list =
          response?.buildings ||
          (Array.isArray(response) ? response : []);
        setBuildings(list);
      } catch (error) {
        console.error('건물 목록 불러오기 실패:', error);
        setBuildings([]);
      } finally {
        setLoadingBuildings(false);
      }
    };
    fetchBuildings();
  }, [filters.property_owner_id, isRMCUser]);

  useEffect(() => {
    // 예정 목록 조회
    const fetchSchedule = async () => {
      if (!filters.property_owner_id) {
        setScheduleList([]);
        return;
      }
      
      // 무한 루프 방지: scheduleRefreshRef가 true면 조회하지 않음 (직접 갱신 중)
      if (scheduleRefreshRef.current) {
        scheduleRefreshRef.current = false;
        return;
      }
      
      try {
        setLoadingSchedule(true);
        const params = new URLSearchParams();
        params.append('property_owner_id', filters.property_owner_id);
        if (filters.building_id) params.append('building_id', filters.building_id);
        if (filters.type) params.append('type', filters.type);
        const response = await api.get(`/rmc/move-in-out/schedule?${params.toString()}`);
        const items = response?.data?.items || response?.items || [];
        setScheduleList(items);
      } catch (error) {
        console.error('입퇴실 예정 목록 조회 실패:', error);
        setScheduleList([]);
      } finally {
        setLoadingSchedule(false);
      }
    };

    // 점검 내역 조회
    const fetchHistory = async () => {
      if (!filters.property_owner_id) {
        setHistoryList([]);
        return;
      }
      try {
        setLoadingHistory(true);
        const params = new URLSearchParams();
        params.append('property_owner_id', filters.property_owner_id);
        if (filters.building_id) params.append('building_id', filters.building_id);
        if (filters.type) params.append('type', filters.type);
        if (filters.month) params.append('month', filters.month);
        const response = await api.get(`/rmc/move-in-out/history?${params.toString()}`);
        const items = response?.data?.items || response?.items || [];
        setHistoryList(items);
      } catch (error) {
        console.error('입퇴실 점검 내역 조회 실패:', error);
        setHistoryList([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    if (activeTab === 'schedule') {
      fetchSchedule();
    } else if (activeTab === 'history') {
      fetchHistory();
    }
    // 의존성 배열을 구체적으로 지정하여 무한 루프 방지
  }, [filters.property_owner_id, filters.building_id, filters.type, filters.month, isRMCUser, activeTab]);

  // 모달이 열릴 때 건물 목록 로드 (한 번만, 버튼 클릭 핸들러에서 이미 처리하므로 제거)
  // useEffect 제거 - 버튼 클릭 핸들러에서 이미 건물 목록을 로드하므로 불필요

  // 건물 선택 시 세대 목록 로드
  // 세대 목록 로드 함수 (중복 호출 방지 - useCallback으로 메모이제이션)
  const loadUnitsForBuildingRef = useRef({ 
    isLoading: false, 
    lastRequest: null,
    lastRequestTime: 0,
    blockedRequests: new Set(), // 403 오류로 차단된 요청들
    lastLoadedUnits: null, // 마지막으로 로드된 세대 목록 (무한 루프 방지)
    isCompleting: false, // 완료 중 플래그 (무한 루프 방지)
    timeoutId: null, // debounce를 위한 timeout ID
    modalOpenBlockUntil: 0 // 모달이 열릴 때 일정 시간 동안 모든 호출 차단
  });
  
  const loadUnitsForBuilding = useCallback(async (buildingId, ownerId, status) => {
    if (!buildingId || !ownerId) {
      loadUnitsForBuildingRef.current.isLoading = false;
      loadUnitsForBuildingRef.current.isCompleting = false;
      // 빈 배열로 설정하는 것도 조건부로
      setModalUnits((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const buildingIdStr = String(buildingId);
    const ownerIdStr = String(ownerId);
    const requestKey = `${buildingIdStr}_${ownerIdStr}_${status}`;

    // 모달이 열린 후 일정 시간 동안 모든 호출 차단 (가장 먼저 체크 - 무한 루프 방지)
    const now = Date.now();
    if (now < loadUnitsForBuildingRef.current.modalOpenBlockUntil) {
      console.log('🚫 모달 열림 차단 시간 동안이므로 호출하지 않음:', {
        requestKey,
        blockUntil: loadUnitsForBuildingRef.current.modalOpenBlockUntil,
        remaining: loadUnitsForBuildingRef.current.modalOpenBlockUntil - now
      });
      return;
    }

    // 완료 중이면 리턴
    if (loadUnitsForBuildingRef.current.isCompleting) {
      console.log('⏸️ 완료 중이므로 호출하지 않음:', requestKey);
      return;
    }

    // 차단된 요청이면 리턴
    if (loadUnitsForBuildingRef.current.blockedRequests && loadUnitsForBuildingRef.current.blockedRequests.has(requestKey)) {
      console.log('🚫 차단된 요청이므로 호출하지 않음:', requestKey);
      return;
    }
    
    // blockedRequests가 없으면 초기화
    if (!loadUnitsForBuildingRef.current.blockedRequests) {
      loadUnitsForBuildingRef.current.blockedRequests = new Set();
    }

    // 이미 같은 요청이 로딩 중이면 리턴
    if (loadUnitsForBuildingRef.current.isLoading) {
      if (loadUnitsForBuildingRef.current.lastRequest === requestKey) {
        console.log('⏸️ 이미 같은 요청이 로딩 중이므로 중복 호출 방지');
        return;
      } else {
        console.log('⏸️ 다른 요청이 로딩 중이므로 대기:', {
          currentRequest: requestKey,
          loadingRequest: loadUnitsForBuildingRef.current.lastRequest
        });
        return;
      }
    }

    // 최근 10초 이내에 같은 요청이 있었으면 리턴
    if (
      loadUnitsForBuildingRef.current.lastRequest === requestKey &&
      now - loadUnitsForBuildingRef.current.lastRequestTime < 10000
    ) {
      console.log('⏸️ 최근에 같은 요청이 있었으므로 중복 호출 방지:', {
        requestKey,
        lastRequestTime: loadUnitsForBuildingRef.current.lastRequestTime,
        elapsed: now - loadUnitsForBuildingRef.current.lastRequestTime
      });
      return;
    }

    console.log('🔵 세대 목록 로드 시작:', {
      buildingId: buildingIdStr,
      ownerId: ownerIdStr,
      status,
      modalType,
      requestKey
    });

    loadUnitsForBuildingRef.current.isLoading = true;
    loadUnitsForBuildingRef.current.isCompleting = true; // 완료 중 플래그 설정
    loadUnitsForBuildingRef.current.lastRequest = requestKey;
    loadUnitsForBuildingRef.current.lastRequestTime = now;
    
    try {
      setLoadingModalUnits(true);

      const params = new URLSearchParams();
      params.append('property_owner_id', ownerIdStr);
      params.append('building_id', buildingIdStr);
      params.append('status', status);
      
      const response = await api.get(`/rmc/move-in-out/units?${params.toString()}`);
      console.log('✅ 세대 목록 API 응답:', response);
      console.log('✅ 응답 타입:', typeof response);
      console.log('✅ 응답 키들:', Object.keys(response || {}));
      console.log('✅ response.data:', response?.data);
      console.log('✅ response.data 타입:', typeof response?.data);
      console.log('✅ response.data 키들:', response?.data ? Object.keys(response.data) : '없음');
      console.log('✅ response.data.units:', response?.data?.units);
      console.log('✅ response.data.units 타입:', typeof response?.data?.units);
      console.log('✅ response.data.units 길이:', response?.data?.units?.length);
      
      const units = response?.data?.units || [];
      console.log('✅ 세대 목록 파싱 결과:', units.length, '개');
      if (units.length > 0) {
        console.log('✅ 첫 번째 세대:', units[0]);
      }
      
      // 마지막으로 로드된 세대 목록과 비교하여 같으면 업데이트하지 않음 (무한 루프 방지)
      const unitsKey = JSON.stringify(units.map(u => ({ id: u.id, unit_number: u.unit_number })));
      if (loadUnitsForBuildingRef.current.lastLoadedUnits === unitsKey) {
        console.log('⏸️ 마지막으로 로드된 세대 목록과 동일하므로 업데이트하지 않음');
      } else {
        loadUnitsForBuildingRef.current.lastLoadedUnits = unitsKey;
        // 조건부 업데이트: 현재 값과 다를 때만 업데이트
        setModalUnits((prev) => {
          const prevKey = JSON.stringify(prev.map(u => ({ id: u.id, unit_number: u.unit_number })));
          if (prevKey === unitsKey) {
            console.log('⏸️ 현재 상태와 동일하므로 업데이트하지 않음');
            return prev;
          }
          return units;
        });
      }
    } catch (error) {
      console.error('❌ 세대 목록 불러오기 실패:', error);
      if (error.response) {
        console.error('❌ 응답 상태:', error.response.status);
        console.error('❌ 응답 데이터:', error.response.data);
        console.error('❌ 요청 파라미터:', { buildingId: buildingIdStr, ownerId: ownerIdStr, status });
        
        // 403 오류인 경우 더 자세한 정보 출력 및 차단
        if (error.response.status === 403) {
          console.error('🚨 403 FORBIDDEN 오류 발생!');
          console.error('🚨 권한 문제일 수 있습니다. 백엔드 로그를 확인하세요.');
          console.error('🚨 요청한 임대사업자 ID:', ownerIdStr);
          console.error('🚨 요청한 건물 ID:', buildingIdStr);
          
          // 이 요청을 차단 목록에 추가 (무한 루프 방지)
          if (!loadUnitsForBuildingRef.current.blockedRequests) {
            loadUnitsForBuildingRef.current.blockedRequests = new Set();
          }
          loadUnitsForBuildingRef.current.blockedRequests.add(requestKey);
          console.error('🚫 이 요청은 차단 목록에 추가되어 더 이상 시도하지 않습니다:', requestKey);
          console.error('🚫 현재 차단된 요청 수:', loadUnitsForBuildingRef.current.blockedRequests.size);
        }
      } else {
        console.error('❌ 네트워크 오류 또는 응답 없음:', error.message);
      }
      // 에러 시에도 조건부로 업데이트
      setModalUnits((prev) => (prev.length === 0 ? prev : []));
    } finally {
      setLoadingModalUnits(false);
      loadUnitsForBuildingRef.current.isLoading = false;
      // 더 긴 지연 후 완료 플래그 해제 (무한 루프 방지)
      setTimeout(() => {
        loadUnitsForBuildingRef.current.isCompleting = false;
        console.log('✅ 완료 플래그 해제');
      }, 3000); // 1초에서 3초로 증가
    }
  }, [modalType]);

  const filteredSchedule = useMemo(() => {
    return scheduleList.filter((item) => {
      const matchOwner = !filters.property_owner_id || String(item.property_owner_id) === String(filters.property_owner_id);
      const matchBuilding = !filters.building_id || String(item.building_id) === String(filters.building_id);
      const matchType = !filters.type || item.type === filters.type;
      return matchOwner && matchBuilding && matchType;
    });
  }, [filters.building_id, filters.property_owner_id, filters.type, scheduleList]);

  const filteredHistory = useMemo(() => {
    return historyList.filter((item) => {
      const matchOwner = !filters.property_owner_id || String(item.property_owner_id) === String(filters.property_owner_id);
      const matchBuilding = !filters.building_id || String(item.building_id) === String(filters.building_id);
      // report_date에서 월 추출하여 비교 (YYYY-MM 형식)
      let matchMonth = true;
      if (filters.month) {
        if (item.report_date) {
          const reportDate = new Date(item.report_date);
          const itemMonth = `${reportDate.getFullYear()}-${String(reportDate.getMonth() + 1).padStart(2, '0')}`;
          matchMonth = itemMonth === filters.month;
        } else {
          matchMonth = false; // report_date가 없으면 필터링에서 제외
        }
      }
      return matchOwner && matchBuilding && matchMonth;
    });
  }, [filters.building_id, filters.month, filters.property_owner_id, historyList]);

  const handleMoveInSubmit = () => {
    if (isRMCUser && !(moveInForm.property_owner_id || filters.property_owner_id)) {
      alert('임대사업자를 선택한 뒤 등록해주세요.');
      return;
    }
    if (!moveInForm.building_id) {
      alert('건물을 선택해주세요.');
      return;
    }
    const payload = {
      ...moveInForm,
      property_owner_id: Number(moveInForm.property_owner_id || filters.property_owner_id),
      building_id: Number(moveInForm.building_id || filters.building_id),
      unit_id: moveInForm.unit_id ? Number(moveInForm.unit_id) : null,
    };
    api.post('/rmc/move-in-out/move-in', payload)
      .then(async () => {
        alert('입실 점검이 등록되었습니다.');
        setMoveInForm(getInitialMoveInForm());
        // 목록 갱신: scheduleRefreshRef를 사용하여 직접 갱신 (무한 루프 방지)
        scheduleRefreshRef.current = true;
        setTimeout(async () => {
          try {
            setLoadingSchedule(true);
            const params = new URLSearchParams();
            params.append('property_owner_id', filters.property_owner_id);
            if (filters.building_id) params.append('building_id', filters.building_id);
            if (filters.type) params.append('type', filters.type);
            const response = await api.get(`/rmc/move-in-out/schedule?${params.toString()}`);
            const items = response?.data?.items || response?.items || [];
            setScheduleList(items);
            scheduleRefreshRef.current = false;
          } catch (error) {
            console.error('❌ 스케줄 목록 갱신 실패:', error);
            scheduleRefreshRef.current = false;
          } finally {
            setLoadingSchedule(false);
          }
        }, 300);
        
        // 입퇴실 내역 리스트도 새로고침
        try {
          setLoadingHistory(true);
          const historyParams = new URLSearchParams();
          historyParams.append('property_owner_id', filters.property_owner_id);
          if (filters.building_id) historyParams.append('building_id', filters.building_id);
          if (filters.type) historyParams.append('type', filters.type);
          if (filters.month) historyParams.append('month', filters.month);
          const historyResponse = await api.get(`/rmc/move-in-out/history?${historyParams.toString()}`);
          const historyItems = historyResponse?.data?.items || historyResponse?.items || [];
          setHistoryList(historyItems);
        } catch (error) {
          console.error('❌ 입퇴실 내역 갱신 실패:', error);
        } finally {
          setLoadingHistory(false);
        }
        
        setActiveTab('schedule'); // 예정 목록 탭으로 이동
      })
      .catch((error) => {
        console.error('입실 점검 등록 실패:', error);
        alert('입실 점검 등록에 실패했습니다.');
      });
  };

  const handleMoveOutSubmit = () => {
    if (isRMCUser && !(moveOutForm.property_owner_id || filters.property_owner_id)) {
      alert('임대사업자를 선택한 뒤 등록해주세요.');
      return;
    }
    if (!moveOutForm.building_id) {
      alert('건물을 선택해주세요.');
      return;
    }
    const payload = {
      ...moveOutForm,
      property_owner_id: Number(moveOutForm.property_owner_id || filters.property_owner_id),
      building_id: Number(moveOutForm.building_id || filters.building_id),
      unit_id: moveOutForm.unit_id ? Number(moveOutForm.unit_id) : null,
    };
    api.post('/rmc/move-in-out/move-out', payload)
      .then(async () => {
        alert('퇴실 점검이 등록되었습니다.');
        setMoveOutForm(getInitialMoveOutForm());
        // 목록 갱신: scheduleRefreshRef를 사용하여 직접 갱신 (무한 루프 방지)
        scheduleRefreshRef.current = true;
        setTimeout(async () => {
          try {
            setLoadingSchedule(true);
            const params = new URLSearchParams();
            params.append('property_owner_id', filters.property_owner_id);
            if (filters.building_id) params.append('building_id', filters.building_id);
            if (filters.type) params.append('type', filters.type);
            const response = await api.get(`/rmc/move-in-out/schedule?${params.toString()}`);
            const items = response?.data?.items || response?.items || [];
            setScheduleList(items);
            scheduleRefreshRef.current = false;
          } catch (error) {
            console.error('❌ 스케줄 목록 갱신 실패:', error);
            scheduleRefreshRef.current = false;
          } finally {
            setLoadingSchedule(false);
          }
        }, 300);
        
        // 입퇴실 내역 리스트도 새로고침 (month 필터 없이 조회하여 최신 데이터 확인)
        try {
          setLoadingHistory(true);
          const historyParams = new URLSearchParams();
          historyParams.append('property_owner_id', filters.property_owner_id);
          if (filters.building_id) historyParams.append('building_id', filters.building_id);
          if (filters.type) historyParams.append('type', filters.type);
          // month 필터는 제외하여 모든 완료된 내역 조회 (방금 등록한 데이터도 포함)
          const historyResponse = await api.get(`/rmc/move-in-out/history?${historyParams.toString()}`);
          const historyItems = historyResponse?.data?.items || historyResponse?.items || [];
          console.log('✅ 입퇴실 내역 갱신 완료:', historyItems.length, '개');
          setHistoryList(historyItems);
        } catch (error) {
          console.error('❌ 입퇴실 내역 갱신 실패:', error);
        } finally {
          setLoadingHistory(false);
        }
        
        setActiveTab('schedule'); // 예정 목록 탭으로 이동
      })
      .catch((error) => {
        console.error('퇴실 점검 등록 실패:', error);
        alert('퇴실 점검 등록에 실패했습니다.');
      });
  };

  // 예정자 목록에서 점검 등록으로 이동하는 핸들러
  const handleScheduleItemClick = async (item) => {
    console.log('📋 예정자 클릭:', item);
    
    try {
      const ownerId = item.property_owner_id || filters.property_owner_id;
      const unitId = item.unit_id;
      
      // 건물 목록 먼저 로드 (폼 채우기 전에 건물 목록이 있어야 함)
      if (ownerId && isRMCUser) {
        try {
          setLoadingBuildings(true);
          const params = new URLSearchParams();
          params.append('property_owner_id', ownerId);
          const response = await api.get(`/rmc/buildings?${params.toString()}`);
          const list = response?.buildings || (Array.isArray(response) ? response : []);
          console.log('📋 건물 목록 로드 완료:', list.length, '개');
          
          // 조건부 업데이트: 현재 값과 다를 때만 업데이트 (무한 루프 방지)
          setBuildings((prev) => {
            const prevIds = prev.map(b => b.id).sort().join(',');
            const newIds = list.map(b => b.id).sort().join(',');
            if (prevIds === newIds) {
              console.log('⏸️ 건물 목록이 동일하므로 업데이트하지 않음');
              return prev;
            }
            return list;
          });
          
          // 건물 목록 로드 완료 후 폼 채우기
          if (item.type === 'move_in') {
            // 입실 점검 등록 폼 채우기
            const initialForm = getInitialMoveInForm();
            let rentInfo = initialForm.rent_info;
            
            // 세대 ID가 있으면 계약 정보 조회
            if (unitId) {
              try {
                const contractResponse = await api.get(`/rmc/move-in-out/contract-details/${unitId}`);
                if (contractResponse?.data?.contract) {
                  const contract = contractResponse.data.contract;
                  rentInfo = [
                    { label: '월세', detail: '선불', amount: contract.monthly_rent || 0 },
                    { label: '관리비', detail: '선불', amount: contract.maintenance_fee || 0 },
                    { label: '주차비', detail: '선불', amount: contract.parking_fee || 0 },
                    { label: '보증금', detail: '보증금', amount: contract.deposit || 0 }
                  ];
                  console.log('✅ 입실 계약 정보 로드 완료:', contract);
                }
              } catch (error) {
                console.error('❌ 계약 정보 조회 실패:', error);
                // 계약 정보 조회 실패해도 기본값으로 진행
              }
            }
            
            setMoveInForm({
              ...initialForm,
              property_owner_id: String(ownerId || ''),
              building_id: String(item.building_id || ''),
              unit_id: item.unit_id || null,
              unit_number: item.unit_number || '',
              tenant_name: item.tenant_name || '',
              phone: item.phone || '',
              expected_date: item.expected_date ? item.expected_date.substring(0, 10) : '',
              rent_info: rentInfo,
              inspector: item.inspector || ''
            });
            console.log('✅ 입실 점검 등록 폼 채움:', {
              property_owner_id: String(ownerId || ''),
              building_id: String(item.building_id || ''),
              unit_number: item.unit_number || '',
              tenant_name: item.tenant_name || '',
              rent_info: rentInfo
            });
            setActiveTab('move_in');
          } else if (item.type === 'move_out') {
            // 퇴실 점검 등록 폼 채우기
            const initialForm = getInitialMoveOutForm();
            let dues = initialForm.dues;
            
            // 세대 ID가 있으면 계약 정보 및 미납액 조회
            if (unitId) {
              try {
                const contractResponse = await api.get(`/rmc/move-in-out/contract-details/${unitId}`);
                if (contractResponse?.data) {
                  const contract = contractResponse.data.contract;
                  const overdueInfo = contractResponse.data.overdue_info || {};
                  
                  // 미납액 정보로 dues 업데이트
                  dues = [];
                  
                  if (overdueInfo.monthly_rent_overdue > 0) {
                    dues.push({
                      label: '미납 월세',
                      detail: '미납액',
                      amount: overdueInfo.monthly_rent_overdue
                    });
                  }
                  
                  if (overdueInfo.maintenance_fee_overdue > 0) {
                    dues.push({
                      label: '미납 관리비',
                      detail: '미납액',
                      amount: overdueInfo.maintenance_fee_overdue
                    });
                  }
                  
                  if (overdueInfo.parking_fee_overdue > 0) {
                    dues.push({
                      label: '미납 주차비',
                      detail: '미납액',
                      amount: overdueInfo.parking_fee_overdue
                    });
                  }
                  
                  if (overdueInfo.other_overdue > 0) {
                    dues.push({
                      label: '기타 미납액',
                      detail: '미납액',
                      amount: overdueInfo.other_overdue
                    });
                  }
                  
                  // 보증금 반환 (음수)
                  if (contract.deposit > 0) {
                    dues.push({
                      label: '보증금 반환',
                      detail: '보증금',
                      amount: -contract.deposit
                    });
                  }
                  
                  // 미납액이 없으면 기본값 유지
                  if (dues.length === 0) {
                    dues = initialForm.dues;
                  }
                  
                  console.log('✅ 퇴실 계약 정보 및 미납액 로드 완료:', { contract, overdueInfo, dues });
                }
              } catch (error) {
                console.error('❌ 계약 정보 및 미납액 조회 실패:', error);
                // 계약 정보 조회 실패해도 기본값으로 진행
              }
            }
            
            setMoveOutForm({
              ...initialForm,
              property_owner_id: String(ownerId || ''),
              building_id: String(item.building_id || ''),
              unit_id: item.unit_id || null,
              unit_number: item.unit_number || '',
              tenant_name: item.tenant_name || '',
              phone: item.phone || '',
              expected_date: item.expected_date ? item.expected_date.substring(0, 10) : '',
              dues: dues,
              inspector: item.inspector || ''
            });
            console.log('✅ 퇴실 점검 등록 폼 채움:', {
              property_owner_id: String(ownerId || ''),
              building_id: String(item.building_id || ''),
              unit_number: item.unit_number || '',
              tenant_name: item.tenant_name || '',
              dues: dues
            });
            setActiveTab('move_out');
          }
        } catch (error) {
          console.error('❌ 건물 목록 불러오기 실패:', error);
          alert('건물 목록을 불러오는 중 오류가 발생했습니다.');
        } finally {
          setLoadingBuildings(false);
        }
      } else {
        // RMC 사용자가 아니거나 ownerId가 없는 경우에도 폼 채우기
        if (item.type === 'move_in') {
          const initialForm = getInitialMoveInForm();
          setMoveInForm({
            ...initialForm,
            property_owner_id: String(ownerId || ''),
            building_id: String(item.building_id || ''),
            unit_number: item.unit_number || '',
            tenant_name: item.tenant_name || '',
            phone: item.phone || '',
            expected_date: item.expected_date ? item.expected_date.substring(0, 10) : '',
            inspector: item.inspector || ''
          });
          setActiveTab('move_in');
        } else if (item.type === 'move_out') {
          const initialForm = getInitialMoveOutForm();
          setMoveOutForm({
            ...initialForm,
            property_owner_id: String(ownerId || ''),
            building_id: String(item.building_id || ''),
            unit_number: item.unit_number || '',
            tenant_name: item.tenant_name || '',
            phone: item.phone || '',
            expected_date: item.expected_date ? item.expected_date.substring(0, 10) : '',
            inspector: item.inspector || ''
          });
          setActiveTab('move_out');
        }
      }
    } catch (error) {
      console.error('❌ 예정자 정보 로드 실패:', error);
      alert('예정자 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 모달에서 예정자 추가 핸들러
  const handleModalSubmit = () => {
    if (isRMCUser && !modalForm.property_owner_id) {
      alert('임대사업자를 선택해주세요.');
      return;
    }
    if (!modalForm.building_id) {
      alert('건물을 선택해주세요.');
      return;
    }
    if (!modalForm.unit_id) {
      alert('세대를 선택해주세요.');
      return;
    }
    if (!modalForm.expected_date) {
      alert('예정일을 입력해주세요.');
      return;
    }
    if (!modalForm.tenant_name) {
      alert('임차인명을 입력해주세요.');
      return;
    }

    const payload = {
      ...modalForm,
      property_owner_id: Number(modalForm.property_owner_id || filters.property_owner_id),
      building_id: Number(modalForm.building_id),
      unit_id: modalForm.unit_id ? Number(modalForm.unit_id) : null,
      // 수동 입력 예정자는 report_date를 보내지 않음 (NULL로 저장되어 스케줄 목록에 표시됨)
      report_date: undefined, // 명시적으로 undefined로 설정하여 백엔드에서 NULL로 저장되도록 함
    };
    
    console.log('🔵 예정자 등록 payload:', payload);

    const endpoint = modalType === 'move_in' ? '/rmc/move-in-out/move-in' : '/rmc/move-in-out/move-out';
    
    api.post(endpoint, payload)
      .then((response) => {
        console.log('✅ 예정자 등록 성공:', response);
        alert(`${modalType === 'move_in' ? '입실' : '퇴실'} 예정자가 등록되었습니다.`);
        setShowAddModal(false);
        setModalForm({
          property_owner_id: '',
          building_id: '',
          unit_id: '',
          unit_number: '',
          tenant_name: '',
          phone: '',
          expected_date: '',
          inspector: '',
          summary: '',
          notes: ''
        });
        setModalUnits([]);
        // 목록 갱신 - 직접 API 호출 (useEffect와 충돌 방지)
        if (activeTab === 'schedule' && filters.property_owner_id) {
          scheduleRefreshRef.current = true; // useEffect에서 조회하지 않도록 플래그 설정
          // 약간의 지연 후 직접 조회
          setTimeout(async () => {
            try {
              setLoadingSchedule(true);
              const params = new URLSearchParams();
              params.append('property_owner_id', filters.property_owner_id);
              if (filters.building_id) params.append('building_id', filters.building_id);
              if (filters.type) params.append('type', filters.type);
              const response = await api.get(`/rmc/move-in-out/schedule?${params.toString()}`);
              const items = response?.data?.items || response?.items || [];
              console.log('✅ 스케줄 목록 갱신 완료:', items.length, '개');
              setScheduleList(items);
              scheduleRefreshRef.current = false; // 플래그 해제
            } catch (error) {
              console.error('❌ 스케줄 목록 갱신 실패:', error);
              scheduleRefreshRef.current = false; // 플래그 해제
            } finally {
              setLoadingSchedule(false);
            }
          }, 300);
        }
      })
      .catch((error) => {
        console.error('예정자 등록 실패:', error);
        alert('예정자 등록에 실패했습니다.');
      });
  };

  // 사진 업로드 핸들러
  const handlePhotoUpload = (e, formType) => {
    const files = Array.from(e.target.files);
    const maxPhotos = 6;
    const currentPhotos = formType === 'move_in' ? moveInForm.photos : moveOutForm.photos;
    
    if (currentPhotos.length + files.length > maxPhotos) {
      alert(`최대 ${maxPhotos}장까지 첨부 가능합니다.`);
      return;
    }

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        if (formType === 'move_in') {
          setMoveInForm((prev) => ({
            ...prev,
            photos: [...prev.photos, { file: base64String, name: file.name }]
          }));
        } else {
          setMoveOutForm((prev) => ({
            ...prev,
            photos: [...prev.photos, { file: base64String, name: file.name }]
          }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // 사진 삭제 핸들러
  const handlePhotoDelete = (index, formType) => {
    if (formType === 'move_in') {
      setMoveInForm((prev) => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index)
      }));
    } else {
      setMoveOutForm((prev) => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index)
      }));
    }
  };

  // 사진 표시 컴포넌트
  const renderPhotoSection = (formType) => {
    const photos = formType === 'move_in' ? moveInForm.photos : moveOutForm.photos;
    const maxPhotos = 6;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            사진 (최대 {maxPhotos}장)
          </label>
          <span className="text-xs text-gray-500">{photos.length}/{maxPhotos}</span>
        </div>
        
        {/* 사진 그리드 */}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={photo.file}
                  alt={`사진 ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => handlePhotoDelete(index, formType)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 사진 업로드 버튼 */}
        {photos.length < maxPhotos && (
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => handlePhotoUpload(e, formType)}
              />
              <div className="flex flex-col items-center space-y-2">
                <Camera className="w-6 h-6 text-gray-400" />
                <span className="text-sm text-gray-600">사진 촬영 또는 선택</span>
              </div>
            </label>
            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handlePhotoUpload(e, formType)}
              />
              <div className="flex flex-col items-center space-y-2">
                <ImageIcon className="w-6 h-6 text-gray-400" />
                <span className="text-sm text-gray-600">갤러리에서 선택</span>
              </div>
            </label>
          </div>
        )}
      </div>
    );
  };

  const renderChecklistTable = (type) => {
    return (
      <div className="space-y-6">
        {CHECKLIST_SECTIONS.map((section) => {
          const sectionKey = section.name;
          return (
            <div key={sectionKey} className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="text-base font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                {sectionKey}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {section.items.map((item) => {
                  const itemKey = `${sectionKey}-${item}`;
                  const isMeter = section.isMeter;
                  const hasNoneOption = section.hasNoneOption;
                  
                  return (
                    <div key={itemKey} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <span className="text-sm font-medium text-gray-800">{item}</span>
                      {type === 'move_in' ? (
                        isMeter ? (
                          <input
                            type="number"
                            className="w-full sm:w-24 text-sm border border-gray-300 rounded-md px-2 py-2 text-right"
                            placeholder="검침지수"
                            value={moveInForm.checklist[itemKey] || ''}
                            onChange={(e) =>
                              setMoveInForm((prev) => ({
                                ...prev,
                                checklist: { ...prev.checklist, [itemKey]: e.target.value }
                              }))
                            }
                          />
                        ) : (
                          <select
                            className="w-full sm:w-auto text-sm border border-gray-300 rounded-md px-2 py-2"
                            value={moveInForm.checklist[itemKey] || '양호'}
                            onChange={(e) =>
                              setMoveInForm((prev) => ({
                                ...prev,
                                checklist: { ...prev.checklist, [itemKey]: e.target.value }
                              }))
                            }
                          >
                            {hasNoneOption && <option value="없음">없음</option>}
                            <option value="양호">양호</option>
                            <option value="보통">보통</option>
                            <option value="불량">불량</option>
                            <option value="미확인">미확인</option>
                          </select>
                        )
                      ) : (
                        <div className="flex items-center space-x-2">
                          {isMeter ? (
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              <input
                                type="number"
                                className="flex-1 sm:w-20 text-sm border border-gray-300 rounded-md px-2 py-2 text-right"
                                placeholder="입실"
                                value={moveOutForm.checklist[itemKey]?.moveInValue || ''}
                                onChange={(e) =>
                                  setMoveOutForm((prev) => ({
                                    ...prev,
                                    checklist: {
                                      ...prev.checklist,
                                      [itemKey]: {
                                        ...prev.checklist[itemKey],
                                        moveInValue: e.target.value
                                      }
                                    }
                                  }))
                                }
                              />
                              <span className="text-xs text-gray-500">→</span>
                              <input
                                type="number"
                                className="flex-1 sm:w-20 text-sm border border-gray-300 rounded-md px-2 py-2 text-right"
                                placeholder="퇴실"
                                value={moveOutForm.checklist[itemKey]?.moveOutValue || ''}
                                onChange={(e) =>
                                  setMoveOutForm((prev) => ({
                                    ...prev,
                                    checklist: {
                                      ...prev.checklist,
                                      [itemKey]: {
                                        ...prev.checklist[itemKey],
                                        moveOutValue: e.target.value
                                      }
                                    }
                                  }))
                                }
                              />
                            </div>
                          ) : (
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                              <select
                                className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-2"
                                value={moveOutForm.checklist[itemKey]?.moveInStatus || '양호'}
                                onChange={(e) =>
                                  setMoveOutForm((prev) => ({
                                    ...prev,
                                    checklist: {
                                      ...prev.checklist,
                                      [itemKey]: { ...prev.checklist[itemKey], moveInStatus: e.target.value }
                                    }
                                  }))
                                }
                              >
                                {hasNoneOption && <option value="없음">입실 없음</option>}
                                <option value="양호">입실 양호</option>
                                <option value="보통">입실 보통</option>
                                <option value="불량">입실 불량</option>
                                <option value="미확인">입실 미확인</option>
                              </select>
                              <select
                                className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-2"
                                value={moveOutForm.checklist[itemKey]?.moveOutStatus || '양호'}
                                onChange={(e) =>
                                  setMoveOutForm((prev) => ({
                                    ...prev,
                                    checklist: {
                                      ...prev.checklist,
                                      [itemKey]: { ...prev.checklist[itemKey], moveOutStatus: e.target.value }
                                    }
                                  }))
                                }
                              >
                                {hasNoneOption && <option value="없음">퇴실 없음</option>}
                                <option value="양호">퇴실 양호</option>
                                <option value="보통">퇴실 보통</option>
                                <option value="불량">퇴실 불량</option>
                                <option value="미확인">퇴실 미확인</option>
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRentTable = (items, onChange, isDeposit = false) => {
    // 항목별 금액 합계 계산
    const totalAmount = items.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    const helperText = isDeposit ? '보증금/차감 내역' : '입금일, 통장 메모 등을 기록하세요.';
    
    return (
      <div className="space-y-3">
        {items.map((row, idx) => (
          <div
            key={`${row.label}-${idx}`}
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:grid md:grid-cols-12 md:items-center md:gap-2 md:border-0 md:bg-transparent md:p-0 md:shadow-none"
          >
            <div className="mb-2 flex items-center justify-between gap-3 md:col-span-2 md:mb-0 md:block">
              <div className="text-sm font-semibold text-gray-900 md:font-medium md:text-gray-700">{row.label}</div>
              <div className="shrink-0 text-sm font-bold text-gray-900 md:hidden">
                {row.amount ? row.amount.toLocaleString() : '0'}원
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 md:contents">
              <label className="block md:col-span-3">
                <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">구분</span>
                <input
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={row.detail}
                  onChange={(e) => onChange(idx, { ...row, detail: e.target.value })}
                />
              </label>
              <label className="block md:col-span-3">
                <span className="mb-1 block text-xs font-medium text-gray-500 md:hidden">금액</span>
                <input
                  type="number"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-right text-sm"
                  value={row.amount}
                  onChange={(e) =>
                    onChange(idx, { ...row, amount: Number(e.target.value || 0) })
                  }
                />
              </label>
            </div>
            <div className="hidden text-right text-sm font-medium text-gray-600 md:col-span-2 md:block">
              {row.amount ? row.amount.toLocaleString() : '0'}원
            </div>
            <div className="mt-2 text-xs leading-5 text-gray-500 md:col-span-2 md:mt-0">
              {helperText}
            </div>
          </div>
        ))}
        {/* 최종 합계 행 (입실점검과 퇴실점검 모두 표시) */}
        <div className="mt-3 flex items-center justify-between rounded-lg border-t-2 border-gray-300 bg-gray-50 px-3 py-3 md:grid md:grid-cols-12 md:gap-2 md:bg-transparent md:px-0">
          <div className="text-sm font-bold text-gray-900 md:col-span-2">합계</div>
          <div className="hidden md:col-span-3 md:block"></div>
          <div className="hidden md:col-span-3 md:block"></div>
          <div className="text-base font-bold text-gray-900 md:col-span-2 md:text-right md:text-sm">
            {totalAmount.toLocaleString()}원
          </div>
          <div className="hidden md:col-span-2 md:block"></div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'schedule', label: '입실/퇴실 예정자 리스트', icon: CalendarClock },
    { id: 'move_in', label: '입실 점검 등록', icon: DoorOpen },
    { id: 'move_out', label: '퇴실 점검 등록', icon: History },
    { id: 'history', label: '입/퇴실 내역', icon: ClipboardList }
  ];

  const openScheduleModal = useCallback((type) => {
    const ownerId = filters.property_owner_id || '';
    setModalForm({
      property_owner_id: String(ownerId),
      building_id: '',
      unit_id: '',
      unit_number: '',
      tenant_name: '',
      phone: '',
      expected_date: '',
      inspector: '',
      summary: '',
      notes: ''
    });
    setModalUnits([]);
    setModalType(type);

    if (loadUnitsForBuildingRef.current) {
      if (loadUnitsForBuildingRef.current.timeoutId) {
        clearTimeout(loadUnitsForBuildingRef.current.timeoutId);
        loadUnitsForBuildingRef.current.timeoutId = null;
      }
      loadUnitsForBuildingRef.current.isLoading = false;
      loadUnitsForBuildingRef.current.isCompleting = false;
      loadUnitsForBuildingRef.current.lastRequest = null;
      loadUnitsForBuildingRef.current.lastRequestTime = 0;
      loadUnitsForBuildingRef.current.lastLoadedUnits = null;
      loadUnitsForBuildingRef.current.modalOpenBlockUntil = Date.now() + 5000;
    }

    setBuildings([]);
    setShowAddModal(true);
  }, [filters.property_owner_id]);

  return (
    <div className="max-w-full overflow-x-hidden p-4 pb-24 sm:p-6 sm:pb-6 space-y-4 sm:space-y-6">
      <div className="flex items-center space-x-2 mb-2">
        <DoorOpen className="w-6 h-6 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">입실 및 퇴실관리</h1>
      </div>
      <p className="text-gray-600">
        RMC 관리자는 임대사업자 선택 후 입실·퇴실 예정 세대를 확인하고 점검표 기반으로 등록합니다.
        모든 업무는 선택된 임대사업자 범위 내에서만 처리됩니다.
      </p>

      {/* 필터 영역 */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
        <div className="flex items-center space-x-2 text-gray-800">
          <ClipboardList className="w-5 h-5 text-primary-600" />
          <span className="font-medium">필터</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {isRMCUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자 *</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={filters.property_owner_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, property_owner_id: e.target.value, building_id: '' }))}
              >
                <option value="">선택</option>
                {propertyOwners.map((owner) => (
                  <option key={owner.id} value={owner.id}>{owner.name}</option>
                ))}
              </select>
              {isRMCUser && !filters.property_owner_id && (
                <p className="mt-1 text-xs text-amber-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" /> 임대사업자 선택 후 목록이 활성화됩니다.
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">건물</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={filters.building_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, building_id: e.target.value }))}
              disabled={isRMCUser && !filters.property_owner_id}
            >
              <option value="">전체</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {loadingBuildings && <p className="text-xs text-gray-500 mt-1">건물 목록을 불러오는 중...</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">구분</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={filters.type}
              onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
            >
              <option value="">전체</option>
              <option value="move_in">입실</option>
              <option value="move_out">퇴실</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">월별(점검 내역)</label>
            <input
              type="month"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={filters.month}
              onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="max-w-full overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="overflow-x-auto border-b border-gray-200">
          <nav className="flex min-w-max -mb-px" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex shrink-0 items-center space-x-2 whitespace-nowrap px-4 py-4 text-sm font-medium border-b-2 transition-colors sm:px-6
                    ${activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="max-w-full p-4 sm:p-6">
          {/* 입실/퇴실 예정자 리스트 */}
          {activeTab === 'schedule' && (
            <div className="space-y-4">
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <div className="flex items-center space-x-2">
                    <CalendarClock className="w-5 h-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900">입실/퇴실 예정 세대</h2>
                  </div>
                  <div className="hidden md:flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => openScheduleModal('move_in')}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4" />
                      <span>입실 예정 추가</span>
                    </button>
                    <button
                      onClick={() => openScheduleModal('move_out')}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-rose-600 text-white rounded-md hover:bg-rose-700 text-sm font-medium whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4" />
                      <span>퇴실 예정 추가</span>
                    </button>
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-500">
                  계약 기반 자동 생성: 계약만료일 15일 이내(퇴실), 입실예정일 3일 이내(입실) | 수동입력 가능
                </div>
              </div>
              <div className="md:hidden space-y-3 pb-24">
                {loadingSchedule && (
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
                    입퇴실 예정자를 불러오는 중입니다...
                  </div>
                )}
                {!loadingSchedule && filteredSchedule.map((item, index) => {
                  const isMoveIn = item.type === 'move_in';
                  return (
                    <div
                      key={item.id || `mobile-auto-${index}`}
                      className={`rounded-lg border p-4 shadow-sm ${
                        isMoveIn ? 'border-green-200 bg-green-50' : 'border-rose-200 bg-rose-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              isMoveIn ? 'bg-green-600 text-white' : 'bg-rose-600 text-white'
                            }`}>
                              {isMoveIn ? '입실' : '퇴실'}
                            </span>
                            <span className="text-sm font-medium text-gray-700">{formatDate(item.expected_date)}</span>
                          </div>
                          <div className="mt-3 text-xl font-bold text-gray-900">
                            {item.unit_number || '-'}호
                          </div>
                          <div className="mt-1 truncate text-sm text-gray-600">{item.building_name || '-'}</div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleScheduleItemClick(item);
                          }}
                          className={`shrink-0 rounded-md px-4 py-3 text-sm font-semibold text-white ${
                            isMoveIn ? 'bg-primary-600 hover:bg-primary-700' : 'bg-rose-600 hover:bg-rose-700'
                          }`}
                        >
                          점검 등록
                        </button>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-md bg-white/80 px-3 py-2">
                          <div className="text-xs text-gray-500">임차인</div>
                          <div className="mt-0.5 font-medium text-gray-900">{item.tenant_name || '-'}</div>
                        </div>
                        <div className="rounded-md bg-white/80 px-3 py-2">
                          <div className="text-xs text-gray-500">연락처</div>
                          {item.phone ? (
                            <a
                              href={`tel:${item.phone}`}
                              className="mt-0.5 inline-flex items-center gap-1 font-semibold text-primary-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone className="h-4 w-4" />
                              전화
                            </a>
                          ) : (
                            <div className="mt-0.5 font-medium text-gray-900">-</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!loadingSchedule && filteredSchedule.length === 0 && (
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
                    필터에 해당하는 예정자가 없습니다.
                  </div>
                )}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">건물명</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">세대호수</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">구분</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">예정일</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">임차인명</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">연락전화번호</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loadingSchedule && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                          입퇴실 예정 세대를 불러오는 중입니다...
                        </td>
                      </tr>
                    )}
                    {!loadingSchedule && filteredSchedule.map((item, index) => (
                      <tr 
                        key={item.id || `auto-${index}`} 
                        className={item.type === 'move_out' ? 'bg-rose-50' : 'bg-green-50'}
                      >
                        <td className="px-3 py-2 text-gray-800">{item.building_name}</td>
                        <td className="px-3 py-2 text-gray-800">{item.unit_number}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {item.type === 'move_in' ? '입실' : '퇴실'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{formatDate(item.expected_date)}</td>
                        <td className="px-3 py-2 text-gray-700">{item.tenant_name || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{item.phone || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('🖱️ 점검 등록 버튼 클릭:', item);
                              handleScheduleItemClick(item);
                            }}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                              item.type === 'move_in' 
                                ? 'bg-primary-600 hover:bg-primary-700 text-white' 
                                : 'bg-rose-600 hover:bg-rose-700 text-white'
                            }`}
                          >
                            점검 등록
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!loadingSchedule && filteredSchedule.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                          필터에 해당하는 예정 세대가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur md:hidden">
                <div className="mx-auto flex max-w-md gap-2">
                  <button
                    onClick={() => openScheduleModal('move_in')}
                    className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700"
                  >
                    <Plus className="h-5 w-5" />
                    <span>입실 예정 추가</span>
                  </button>
                  <button
                    onClick={() => openScheduleModal('move_out')}
                    className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-3 text-sm font-semibold text-white hover:bg-rose-700"
                  >
                    <Plus className="h-5 w-5" />
                    <span>퇴실 예정 추가</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 입실 점검 등록 */}
          {activeTab === 'move_in' && (
            <div className="bg-white space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <DoorOpen className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">입실 점검 및 등록</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">점검자</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.inspector}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, inspector: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">작성일</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.report_date}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, report_date: e.target.value }))}
                  />
                </div>
                {isRMCUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={moveInForm.property_owner_id}
                      onChange={(e) => setMoveInForm((prev) => ({ ...prev, property_owner_id: e.target.value, building_id: '' }))}
                    >
                      <option value="">선택</option>
                      {propertyOwners.map((owner) => (
                        <option key={owner.id} value={owner.id}>{owner.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">건물명</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.building_id}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, building_id: e.target.value }))}
                    disabled={isRMCUser && !(moveInForm.property_owner_id || filters.property_owner_id)}
                  >
                    <option value="">선택</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">세대호수</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.unit_number}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, unit_number: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">입실 예정일</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.expected_date}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, expected_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">임차인명</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.tenant_name}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, tenant_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">연락전화번호</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveInForm.phone}
                    onChange={(e) => setMoveInForm((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-semibold text-gray-800">월세/관리비 및 보증금 내역</span>
                </div>
                {renderRentTable(moveInForm.rent_info, (idx, row) =>
                  setMoveInForm((prev) => {
                    const next = [...prev.rent_info];
                    next[idx] = row;
                    return { ...prev, rent_info: next };
                  })
                )}
              </div>

              <div className="p-3 border border-gray-200 rounded-lg space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckSquare className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-semibold text-gray-800">입실 점검표</span>
                </div>
                {renderChecklistTable('move_in')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">특이사항/비고</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  value={moveInForm.notes}
                  onChange={(e) => setMoveInForm((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              {/* 사진 섹션 */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                {renderPhotoSection('move_in')}
              </div>

              {/* 사인 패드 */}
              <div className="p-4 border border-gray-200 rounded-lg bg-white">
                <SignaturePad
                  value={moveInForm.signature}
                  onChange={(value) => setMoveInForm((prev) => ({ ...prev, signature: value }))}
                  label="임차인 확인 서명"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setMoveInForm(getInitialMoveInForm())}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  초기화
                </button>
                <button
                  onClick={handleMoveInSubmit}
                  className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
                >
                  입실 점검 등록
                </button>
              </div>
            </div>
          )}

          {/* 퇴실 점검 등록 */}
          {activeTab === 'move_out' && (
            <div className="bg-white space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <History className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">퇴실 점검 및 등록</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">점검자</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.inspector}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, inspector: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">작성일</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.report_date}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, report_date: e.target.value }))}
                  />
                </div>
                {isRMCUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={moveOutForm.property_owner_id}
                      onChange={(e) => setMoveOutForm((prev) => ({ ...prev, property_owner_id: e.target.value, building_id: '' }))}
                    >
                      <option value="">선택</option>
                      {propertyOwners.map((owner) => (
                        <option key={owner.id} value={owner.id}>{owner.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">건물명</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.building_id}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, building_id: e.target.value }))}
                    disabled={isRMCUser && !(moveOutForm.property_owner_id || filters.property_owner_id)}
                  >
                    <option value="">선택</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">세대호수</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.unit_number}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, unit_number: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">퇴실 예정일</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.expected_date}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, expected_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">퇴실자명</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.tenant_name}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, tenant_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">연락전화번호</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={moveOutForm.phone}
                    onChange={(e) => setMoveOutForm((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 space-y-2">
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-semibold text-gray-800">미납금 및 보증금 정산</span>
                </div>
                {renderRentTable(moveOutForm.dues, (idx, row) =>
                  setMoveOutForm((prev) => {
                    const next = [...prev.dues];
                    next[idx] = row;
                    return { ...prev, dues: next };
                  }), true)}
              </div>

              <div className="p-3 border border-gray-200 rounded-lg space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckSquare className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-semibold text-gray-800">퇴실 점검표</span>
                </div>
                {renderChecklistTable('move_out')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">특이사항/정산 메모</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  value={moveOutForm.notes}
                  onChange={(e) => setMoveOutForm((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              {/* 사진 섹션 */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                {renderPhotoSection('move_out')}
              </div>

              {/* 사인 패드 */}
              <div className="p-4 border border-gray-200 rounded-lg bg-white">
                <SignaturePad
                  value={moveOutForm.signature}
                  onChange={(value) => setMoveOutForm((prev) => ({ ...prev, signature: value }))}
                  label="퇴실자 확인 서명"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setMoveOutForm(getInitialMoveOutForm())}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  초기화
                </button>
                <button
                  onClick={handleMoveOutSubmit}
                  className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
                >
                  퇴실 점검 등록
                </button>
              </div>
            </div>
          )}

          {/* 입/퇴실 내역 */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <History className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">입/퇴실 점검 내역</h3>
                <span className="text-sm text-gray-500">(월별/임대사업자별/건물별 필터링)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">구분</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">작성일</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">건물명</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">세대호수</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">임차인/퇴실자</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">연락처</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">요약</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loadingHistory && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                          점검 내역을 불러오는 중입니다...
                        </td>
                      </tr>
                    )}
                    {!loadingHistory && filteredHistory.map((item) => (
                      <tr key={item.id}>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {item.type === 'move_in' ? '입실' : '퇴실'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{formatDate(item.report_date)}</td>
                        <td className="px-3 py-2 text-gray-700">{item.building_name}</td>
                        <td className="px-3 py-2 text-gray-700">{item.unit_number}</td>
                        <td className="px-3 py-2 text-gray-700">{item.tenant_name}</td>
                        <td className="px-3 py-2 text-gray-700">{item.phone}</td>
                        <td className="px-3 py-2 text-gray-700">{item.summary}</td>
                      </tr>
                    ))}
                    {!loadingHistory && filteredHistory.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                          필터에 해당하는 점검 내역이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 예정자 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalType === 'move_in' ? '입실' : '퇴실'} 예정자 추가
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {isRMCUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">임대사업자 *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={modalForm.property_owner_id}
                    onChange={async (e) => {
                      const selectedOwnerId = e.target.value;
                      setModalForm((prev) => ({ ...prev, property_owner_id: selectedOwnerId, building_id: '', unit_id: '', unit_number: '', tenant_name: '', phone: '' }));
                      setModalUnits([]);
                      
                      // 건물 목록 다시 로드
                      if (selectedOwnerId && isRMCUser) {
                        try {
                          const params = new URLSearchParams();
                          params.append('property_owner_id', selectedOwnerId);
                          const response = await api.get(`/rmc/buildings?${params.toString()}`);
                          // API 인터셉터가 response.data를 반환하므로 response 자체가 이미 data입니다
                          const list =
                            response?.buildings ||
                            (Array.isArray(response) ? response : []);
                          console.log('임대사업자 선택 시 건물 목록 로드:', list.length, '개');
                          // 조건부 업데이트: 현재 값과 다를 때만 업데이트 (무한 루프 방지)
                          setBuildings((prev) => {
                            const prevIds = prev.map(b => b.id).sort().join(',');
                            const newIds = list.map(b => b.id).sort().join(',');
                            if (prevIds === newIds) {
                              console.log('⏸️ 건물 목록이 동일하므로 업데이트하지 않음');
                              return prev;
                            }
                            return list;
                          });
                        } catch (error) {
                          console.error('건물 목록 불러오기 실패:', error);
                        }
                      } else {
                        setBuildings([]);
                      }
                    }}
                  >
                    <option value="">선택</option>
                    {propertyOwners.map((owner) => (
                      <option key={owner.id} value={owner.id}>{owner.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">건물 *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={modalForm.building_id || ''}
                  onClick={(e) => {
                    console.log('🏢 건물 select 클릭:', {
                      currentValue: e.target.value,
                      modalForm_building_id: modalForm.building_id,
                      modalForm_property_owner_id: modalForm.property_owner_id,
                      filters_property_owner_id: filters.property_owner_id,
                      disabled: e.target.disabled,
                      buildings_count: buildings.length
                    });
                  }}
                  onFocus={(e) => {
                    console.log('🏢 건물 select 포커스:', {
                      currentValue: e.target.value,
                      modalForm_building_id: modalForm.building_id,
                      modalForm_property_owner_id: modalForm.property_owner_id,
                      filters_property_owner_id: filters.property_owner_id,
                      disabled: e.target.disabled,
                      buildings_count: buildings.length
                    });
                  }}
                  onChange={async (e) => {
                    // 모달 열림 차단 시간 확인 (무한 루프 방지)
                    const now = Date.now();
                    if (now < loadUnitsForBuildingRef.current.modalOpenBlockUntil) {
                      console.log('🚫 모달 열림 차단 시간 동안이므로 건물 선택 핸들러 실행하지 않음:', {
                        blockUntil: loadUnitsForBuildingRef.current.modalOpenBlockUntil,
                        remaining: loadUnitsForBuildingRef.current.modalOpenBlockUntil - now
                      });
                      return;
                    }
                    
                    const selectedBuildingId = e.target.value;
                    const ownerId = modalForm.property_owner_id || filters.property_owner_id;
                    
                    console.log('🏢🏢🏢 건물 선택 핸들러 실행! 🏢🏢🏢');
                    console.log('🏢 건물 선택됨:', {
                      selectedBuildingId,
                      selectedBuildingIdType: typeof selectedBuildingId,
                      selectedBuildingIdEmpty: !selectedBuildingId,
                      current_property_owner_id: modalForm.property_owner_id,
                      filters_property_owner_id: filters.property_owner_id,
                      ownerId,
                      ownerIdType: typeof ownerId,
                      ownerIdEmpty: !ownerId,
                      modalType
                    });
                    
                    // 건물 선택 해제 시
                    if (!selectedBuildingId || selectedBuildingId === '') {
                      console.log('⚠️ 건물 선택이 해제되었습니다.');
                      setModalForm((prev) => ({
                        ...prev,
                        building_id: '',
                        unit_id: '',
                        unit_number: '',
                        tenant_name: '',
                        phone: ''
                      }));
                      setModalUnits([]);
                      return;
                    }
                    
                    if (!ownerId) {
                      console.error('❌ 임대사업자가 선택되지 않았습니다!');
                      alert('임대사업자를 먼저 선택해주세요.');
                      return;
                    }
                    
                    // modalForm 업데이트 (먼저 업데이트)
                    setModalForm((prev) => {
                      const newForm = { 
                        ...prev, 
                        property_owner_id: String(ownerId), // 문자열로 확실히 설정
                        building_id: String(selectedBuildingId), // 문자열로 확실히 설정
                        unit_id: '',
                        unit_number: '',
                        tenant_name: '',
                        phone: ''
                      };
                      console.log('🏢 modalForm 업데이트:', newForm);
                      return newForm;
                    });
                    
                    // debounce를 사용하여 무한 루프 방지 (더 긴 지연)
                    const status = modalType === 'move_in' ? 'available' : 'occupied';
                    
                    // 이전 timeout 취소
                    if (loadUnitsForBuildingRef.current.timeoutId) {
                      clearTimeout(loadUnitsForBuildingRef.current.timeoutId);
                      console.log('⏸️ 이전 timeout 취소');
                    }
                    
                    // 세대 목록 초기화는 loadUnitsForBuilding 내부에서 처리하도록 함
                    // 여기서는 초기화하지 않음 (무한 루프 방지)
                    
                    // 새로운 timeout 설정 (1500ms 지연 - 더 길게)
                    loadUnitsForBuildingRef.current.timeoutId = setTimeout(() => {
                      console.log('🏢 건물 선택 후 세대 목록 로드 시작 (debounce 후)');
                      loadUnitsForBuildingRef.current.timeoutId = null;
                      // 완료 플래그 확인 후 호출
                      if (!loadUnitsForBuildingRef.current.isCompleting && !loadUnitsForBuildingRef.current.isLoading) {
                        loadUnitsForBuilding(selectedBuildingId, ownerId, status);
                      } else {
                        console.log('⏸️ 완료 중이거나 로딩 중이므로 호출하지 않음:', {
                          isCompleting: loadUnitsForBuildingRef.current.isCompleting,
                          isLoading: loadUnitsForBuildingRef.current.isLoading
                        });
                      }
                    }, 1500);
                  }}
                  disabled={isRMCUser && !(modalForm.property_owner_id || filters.property_owner_id)}
                >
                  <option value="">선택</option>
                  {buildings.map((b) => {
                    console.log('🏢 건물 렌더링:', { id: b.id, name: b.name });
                    return (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    );
                  })}
                  {buildings.length === 0 && (modalForm.property_owner_id || filters.property_owner_id) && (
                    <option value="" disabled>건물이 없습니다 (로딩 중...)</option>
                  )}
                </select>
                {showAddModal && (
                  <div className="mt-1 text-xs text-gray-500">
                    건물 수: {buildings.length}개
                    {(() => {
                      const ownerId = modalForm.property_owner_id || filters.property_owner_id;
                      if (ownerId) {
                        const filtered = buildings.filter(b => b.property_owner_id === Number(ownerId));
                        return ` (필터링 후: ${filtered.length}개)`;
                      }
                      return '';
                    })()}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">세대 *</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={modalForm.unit_id}
                  onChange={async (e) => {
                    const selectedUnitId = e.target.value;
                    const selectedUnit = modalUnits.find(u => u.id === Number(selectedUnitId) || u.unit_id === Number(selectedUnitId));
                    
                    if (selectedUnit) {
                      // 세대 정보 자동 채우기
                      const unitId = selectedUnit.id || selectedUnit.unit_id;
                      const unitNumber = selectedUnit.unit_number;
                      
                      setModalForm((prev) => ({
                        ...prev,
                        unit_id: unitId,
                        unit_number: unitNumber
                      }));

                      // 퇴실의 경우 임차인 정보 자동 채우기 (세대 목록에 이미 포함됨)
                      if (modalType === 'move_out' && selectedUnit.tenant_name) {
                        setModalForm((prev) => ({
                          ...prev,
                          tenant_name: selectedUnit.tenant_name || '',
                          phone: selectedUnit.phone || '',
                          expected_date: selectedUnit.contract_end_date || prev.expected_date
                        }));
                        
                        // 퇴실의 경우: 계약 정보 및 미납액 조회
                        try {
                          const contractResponse = await api.get(`/rmc/move-in-out/contract-details/${unitId}`);
                          if (contractResponse?.data) {
                            const contract = contractResponse.data.contract;
                            const overdueInfo = contractResponse.data.overdue_info || {};
                            
                            console.log('✅ 모달 퇴실 계약 정보 및 미납액 로드 완료:', { contract, overdueInfo });
                            // 미납액 정보는 퇴실 점검 등록 시에만 사용되므로 여기서는 로그만 남김
                            // 실제 폼 채우기는 점검 등록 탭에서 처리
                          }
                        } catch (error) {
                          console.error('❌ 모달 계약 정보 및 미납액 조회 실패:', error);
                        }
                      }
                      
                      // 입실의 경우: 공실 세대이므로 계약 정보 조회 불필요
                      // 입실 예정일은 사용자가 직접 입력하거나 다른 방법으로 설정
                    } else {
                      setModalForm((prev) => ({
                        ...prev,
                        unit_id: '',
                        unit_number: '',
                        tenant_name: '',
                        phone: ''
                      }));
                    }
                  }}
                  disabled={!modalForm.building_id || loadingModalUnits}
                >
                  <option value="">선택</option>
                  {loadingModalUnits ? (
                    <option value="" disabled>로딩 중...</option>
                  ) : modalUnits.length === 0 && modalForm.building_id ? (
                    <option value="" disabled>
                      {modalType === 'move_in' ? '공실 세대가 없습니다.' : '임대중인 세대가 없습니다.'}
                    </option>
                  ) : modalUnits.length === 0 ? (
                    <option value="" disabled>건물을 먼저 선택해주세요.</option>
                  ) : (
                    modalUnits.map((unit) => {
                      const unitId = unit.id || unit.unit_id;
                      const unitNumber = unit.unit_number;
                      const displayText = unitNumber || `세대 ID: ${unitId}`;
                      console.log('🏠 세대 렌더링:', { unitId, unitNumber, displayText, unit });
                      return (
                        <option key={unitId} value={unitId}>
                          {displayText}
                        </option>
                      );
                    })
                  )}
                </select>
                {modalForm.building_id && modalUnits.length === 0 && !loadingModalUnits && (
                  <p className="mt-1 text-sm text-gray-500">
                    {modalType === 'move_in' ? '공실 세대가 없습니다.' : '임대중인 세대가 없습니다.'}
                  </p>
                )}
              </div>

              {modalForm.unit_number && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">세대호수</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    value={modalForm.unit_number}
                    readOnly
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">임차인명 *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={modalForm.tenant_name}
                  onChange={(e) => setModalForm((prev) => ({ ...prev, tenant_name: e.target.value }))}
                  placeholder="임차인 이름"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={modalForm.phone}
                  onChange={(e) => setModalForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예정일 *</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={modalForm.expected_date}
                  onChange={(e) => setModalForm((prev) => ({ ...prev, expected_date: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">점검자</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={modalForm.inspector}
                  onChange={(e) => setModalForm((prev) => ({ ...prev, inspector: e.target.value }))}
                  placeholder="점검자 이름"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">요약</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="2"
                  value={modalForm.summary}
                  onChange={(e) => setModalForm((prev) => ({ ...prev, summary: e.target.value }))}
                  placeholder="요약 정보"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">비고</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="2"
                  value={modalForm.notes}
                  onChange={(e) => setModalForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="추가 메모"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setModalForm({
                    property_owner_id: '',
                    building_id: '',
                    unit_id: '',
                    unit_number: '',
                    tenant_name: '',
                    phone: '',
                    expected_date: '',
                    inspector: '',
                    summary: '',
                    notes: ''
                  });
                  setModalUnits([]);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleModalSubmit}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                등록
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoveInOutManagement;
